import React, { FC, useMemo, useState } from 'react';
import { Button, Message, Tag } from '@bedrock/components';
import { ChevronDown, ChevronUp, Copy } from '@bedrock/icons-react';
import { ProTablePlus } from '@bedrock/eed-react';
import { EllipsisTooltip } from '@mpc/components';
import copy from 'copy-to-clipboard';

const STATUS_COLOR_MAP: Record<string, string> = {
  有效: 'light-green',
  过期失效: 'light-red',
};

const uniq = (arr: any[], key: string) => {
  const seen = new Set<string>();
  arr.forEach((i) => {
    const v = i?.[key];
    if (v) seen.add(v);
  });
  return Array.from(seen).map((v) => ({ name: v, value: v }));
};

export interface QccCertificateTableProps {
  list: any[];
  defaultList: any[];
  hasExtra: boolean;
}

const QccCertificateTable: FC<QccCertificateTableProps> = ({
  list,
  defaultList,
  hasExtra,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState<{ typeDesc?: string }>({});

  const typeDescOptions = useMemo(() => uniq(list, 'typeDesc'), [list]);

  const data = useMemo(() => {
    if (filter.typeDesc) {
      return list.filter((i) => i.typeDesc === filter.typeDesc);
    }
    return showAll ? list : defaultList;
  }, [list, defaultList, showAll, filter.typeDesc]);

  const columns = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'rowIndex',
        width: 50,
        fixed: true,
        render: (_: unknown, __: unknown, index: number) => index + 1,
      },
      {
        title: '产品名称/资质类别',
        dataIndex: 'name',
        width: 200,
        fixed: true,
        className: 'font-semibold',
        render: (v?: string) => v || '-',
      },
      {
        title: '证书生效时间',
        dataIndex: 'startDate',
        width: 130,
        render: (v?: string) => v || '-',
      },
      {
        title: '证书截止日期',
        dataIndex: 'endDate',
        width: 130,
        render: (v?: string) => v || '-',
      },
      {
        title: '证书编号',
        dataIndex: 'no',
        render: (v?: string) => {
          if (!v) return '-';
          return (
            <div className="flex items-center">
              <EllipsisTooltip text={v} />
              <Copy
                className="ml-[4px]"
                style={{ color: '#68768F' }}
                onClick={() => {
                  copy(v);
                  Message.success({ content: '复制成功' });
                }}
              />
            </div>
          );
        },
      },
      {
        title: '证书类型描述',
        dataIndex: 'typeDesc',
        filterRender: {
          name: 'select',
          props: { options: typeDescOptions, multiple: false },
        },
        render: (v?: string) => v || '-',
      },
      {
        title: '发证机构',
        dataIndex: 'institutionList',
        render: (value?: string[]) => {
          if (!value || value.length === 0) return '-';
          return value.join('、');
        },
      },
      {
        title: '证书状态',
        dataIndex: 'status',
        width: 140,
        render: (value?: string) => {
          if (!value) return '-';
          return (
            <Tag color={STATUS_COLOR_MAP[value]} size="xSmall" text={value} />
          );
        },
      },
    ],
    [typeDescOptions]
  );

  return (
    <>
      <ProTablePlus
        columns={columns as any}
        data={data}
        pagination={false}
        scroll={{ x: 'max-content' }}
        onFilterChange={
          ((_dataIndex: string, filterValue: Record<string, any>) =>
            setFilter({
              typeDesc: filterValue.typeDesc ?? '',
            })) as any
        }
      />

      {hasExtra && !filter.typeDesc && (
        <div className="mt-2 text-xs text-color-2">
          <Button
            type="link"
            onClick={() => setShowAll((prev) => !prev)}
            suffixIcon={showAll ? <ChevronUp /> : <ChevronDown />}
          >
            {showAll ? '收起' : '展开全部'}
          </Button>
        </div>
      )}
    </>
  );
};

export default QccCertificateTable;
